import { differenceInYears, isValid } from 'date-fns';
import React, { useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { FormInput, FormPhoneNumberInput, FormSection, getFieldName, VerticalLayout } from 'widgets';
import { FormDateInput } from 'widgets/form/input/FormDateInput';

const calculateAge = (dateOfBirth: string | null | undefined): number | null => {
    if (!dateOfBirth) return null;

    const birthDate = new Date(dateOfBirth);
    if (!isValid(birthDate)) return null;

    return differenceInYears(new Date(), birthDate);
};

export const PersonBasicFormFields = ({
    prefix = '',
    hasDivider = false,
}: {
    prefix?: string;
    hasDivider?: boolean;
}) => {
    return (
        <>
            <PersonFormSection prefix={prefix} hasDivider={hasDivider} />
            <ContactInformationFormSection prefix={prefix} hasDivider={hasDivider} />
        </>
    );
};

export const PersonFormSection = ({
    title = 'Person Details',
    className,
    prefix = '',
    hasDivider = false,
}: {
    prefix?: string;
    className?: string;
    hasDivider?: boolean;
    title?: string;
}) => {
    const dateOfBirth = useWatch({ name: getFieldName('dateOfBirth', prefix) });

    const dateOfBirthLabel = useMemo(() => {
        const age = calculateAge(dateOfBirth);
        return age !== null ? `Date of birth (Age: ${age})` : 'Date of birth';
    }, [dateOfBirth]);

    return (
        <FormSection title={title} hasDivider={hasDivider} className={className}>
            <VerticalLayout $gap={4}>
                <FormInput name={getFieldName('firstName', prefix)} placeholder="Johnny" label="First name" />
                <FormInput name={getFieldName('lastName', prefix)} placeholder="Appleseed" label="Last name" />
                <FormDateInput name={getFieldName('dateOfBirth', prefix)} label={dateOfBirthLabel} />
            </VerticalLayout>
        </FormSection>
    );
};

export const ContactInformationFormSection = ({
    title = 'Contact Information',
    className,
    prefix = '',
    hasDivider = false,
}: {
    prefix?: string;
    className?: string;
    hasDivider?: boolean;
    title?: string;
}) => {
    return (
        <FormSection title={title} hasDivider={hasDivider} className={className}>
            <VerticalLayout $gap={4}>
                <FormInput
                    name={getFieldName('primaryEmail', prefix)}
                    placeholder="johnnyappleseed@gmail.com"
                    label="Primary Email (username)"
                />
                <FormInput
                    name={getFieldName('otherEmail', prefix)}
                    placeholder="jimmyappleseed@gmail.com"
                    label="Alternate email"
                />
                <FormPhoneNumberInput name={getFieldName('personalPhone', prefix)} label="Phone number" />
                <FormPhoneNumberInput name={getFieldName('businessPhone', prefix)} label="Business phone" />
            </VerticalLayout>
        </FormSection>
    );
};
